import React, { useState, useEffect, useRef } from 'react';

const BiomarkerSearch = ({ data }) => {
  const [selectedBiomarkers, setSelectedBiomarkers] = useState([]);
  const [selectedDisorders, setSelectedDisorders] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [disorders, setDisorders] = useState([]);
  const [regions, setRegions] = useState([]);
  const [biomarkers, setBiomarkers] = useState([]);
  const [increasedResults, setIncreasedResults] = useState([]);
  const [decreasedResults, setDecreasedResults] = useState([]);
  
  const [biomarkerSearchTerm, setBiomarkerSearchTerm] = useState('');
  const [isBiomarkerDropdownOpen, setIsBiomarkerDropdownOpen] = useState(false);
  
  const [disorderSearchTerm, setDisorderSearchTerm] = useState('');
  const [isDisorderDropdownOpen, setIsDisorderDropdownOpen] = useState(false);
  
  const [regionSearchTerm, setRegionSearchTerm] = useState('');
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);

  const biomarkerDropdownRef = useRef(null);
  const disorderDropdownRef = useRef(null);
  const regionDropdownRef = useRef(null);

  useEffect(() => {
    const uniqueDisorders = [...new Set(data.map(item => item.disorder))].filter(d => d);
    const uniqueRegions = [...new Set(data.map(item => item.region))].filter(r => r);
    const uniqueBiomarkers = [...new Set(data.map(item => item.biomarker))].filter(b => b);
    
    setDisorders(uniqueDisorders);
    setRegions(uniqueRegions);
    setBiomarkers(uniqueBiomarkers);
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (biomarkerDropdownRef.current && !biomarkerDropdownRef.current.contains(event.target)) {
        setIsBiomarkerDropdownOpen(false);
      }
      if (disorderDropdownRef.current && !disorderDropdownRef.current.contains(event.target)) {
        setIsDisorderDropdownOpen(false);
      }
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(event.target)) {
        setIsRegionDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredBiomarkers = biomarkers.filter(biomarker =>
    biomarker.toLowerCase().includes(biomarkerSearchTerm.toLowerCase())
  );

  const filteredDisorders = disorders.filter(disorder =>
    disorder.toLowerCase().includes(disorderSearchTerm.toLowerCase())
  );

  const filteredRegions = regions.filter(region =>
    region.toLowerCase().includes(regionSearchTerm.toLowerCase())
  );

  const handleBiomarkerSelect = (biomarker) => {
    if (!selectedBiomarkers.includes(biomarker)) {
      setSelectedBiomarkers(prev => [...prev, biomarker]);
    }
    setBiomarkerSearchTerm('');
    setIsBiomarkerDropdownOpen(false);
  };

  const removeBiomarker = (biomarkerToRemove) => {
    setSelectedBiomarkers(prev => prev.filter(b => b !== biomarkerToRemove));
  };

  const selectAllBiomarkers = () => {
    const biomarkersToSelect = biomarkerSearchTerm ? filteredBiomarkers : biomarkers;
    setSelectedBiomarkers(prev => {
      const combined = [...new Set([...prev, ...biomarkersToSelect])];
      return combined;
    });
    setBiomarkerSearchTerm('');
    setIsBiomarkerDropdownOpen(false);
  };

  const selectNoneBiomarkers = () => {
    setSelectedBiomarkers([]);
  };

  const areAllFilteredBiomarkersSelected = () => {
    const biomarkersToCheck = biomarkerSearchTerm ? filteredBiomarkers : biomarkers;
    return biomarkersToCheck.length > 0 && biomarkersToCheck.every(biomarker => 
      selectedBiomarkers.includes(biomarker)
    );
  };

  const toggleSelectAllFilteredBiomarkers = () => {
    if (areAllFilteredBiomarkersSelected()) {
      const biomarkersToRemove = biomarkerSearchTerm ? filteredBiomarkers : biomarkers;
      setSelectedBiomarkers(prev => 
        prev.filter(biomarker => !biomarkersToRemove.includes(biomarker))
      );
    } else {
      selectAllBiomarkers();
    }
  };

  const clearAllBiomarkers = () => {
    setSelectedBiomarkers([]);
    setBiomarkerSearchTerm('');
  };

  const handleDisorderSelect = (disorder) => {
    if (!selectedDisorders.includes(disorder)) {
      setSelectedDisorders(prev => [...prev, disorder]);
    }
    setDisorderSearchTerm('');
    setIsDisorderDropdownOpen(false);
  };

  const removeDisorder = (disorderToRemove) => {
    setSelectedDisorders(prev => prev.filter(d => d !== disorderToRemove));
  };

  const selectAllDisorders = () => {
    const disordersToSelect = disorderSearchTerm ? filteredDisorders : disorders;
    setSelectedDisorders(prev => {
      const combined = [...new Set([...prev, ...disordersToSelect])];
      return combined;
    });
    setDisorderSearchTerm('');
    setIsDisorderDropdownOpen(false);
  };

  const selectNoneDisorders = () => {
    setSelectedDisorders([]);
  };

  const areAllFilteredDisordersSelected = () => {
    const disordersToCheck = disorderSearchTerm ? filteredDisorders : disorders;
    return disordersToCheck.length > 0 && disordersToCheck.every(disorder => 
      selectedDisorders.includes(disorder)
    );
  };

  const toggleSelectAllFilteredDisorders = () => {
    if (areAllFilteredDisordersSelected()) {
      const disordersToRemove = disorderSearchTerm ? filteredDisorders : disorders;
      setSelectedDisorders(prev => 
        prev.filter(disorder => !disordersToRemove.includes(disorder))
      );
    } else {
      selectAllDisorders();
    }
  };

  const clearAllDisorders = () => {
    setSelectedDisorders([]);
    setDisorderSearchTerm('');
  };

  const handleRegionSelect = (region) => {
    if (!selectedRegions.includes(region)) {
      setSelectedRegions(prev => [...prev, region]);
    }
    setRegionSearchTerm('');
    setIsRegionDropdownOpen(false);
  };

  const removeRegion = (regionToRemove) => {
    setSelectedRegions(prev => prev.filter(r => r !== regionToRemove));
  };

  const selectAllRegions = () => {
    const regionsToSelect = regionSearchTerm ? filteredRegions : regions;
    setSelectedRegions(prev => {
      const combined = [...new Set([...prev, ...regionsToSelect])];
      return combined;
    });
    setRegionSearchTerm('');
    setIsRegionDropdownOpen(false);
  };

  const selectNoneRegions = () => {
    setSelectedRegions([]);
  };

  const areAllFilteredRegionsSelected = () => {
    const regionsToCheck = regionSearchTerm ? filteredRegions : regions;
    return regionsToCheck.length > 0 && regionsToCheck.every(region => 
      selectedRegions.includes(region)
    );
  };

  const toggleSelectAllFilteredRegions = () => {
    if (areAllFilteredRegionsSelected()) {
      const regionsToRemove = regionSearchTerm ? filteredRegions : regions;
      setSelectedRegions(prev => 
        prev.filter(region => !regionsToRemove.includes(region))
      );
    } else {
      selectAllRegions();
    }
  };

  const clearAllRegions = () => {
    setSelectedRegions([]);
    setRegionSearchTerm('');
  };

  const handleSearch = () => {
    if (selectedBiomarkers.length === 0) {
      alert('Please select at least one biomarker');
      return;
    }

    let filteredData = data.filter(item => 
      selectedBiomarkers.includes(item.biomarker)
    );

    if (selectedDisorders.length > 0) {
      filteredData = filteredData.filter(item => selectedDisorders.includes(item.disorder));
    }

    if (selectedRegions.length > 0) {
      filteredData = filteredData.filter(item => selectedRegions.includes(item.region));
    }

    const increased = filteredData.filter(item => item.change === 'Increase');
    const decreased = filteredData.filter(item => item.change === 'Decrease');

    setIncreasedResults(increased);
    setDecreasedResults(decreased);
  };

  const clearResults = () => {
    setIncreasedResults([]);
    setDecreasedResults([]);
    setSelectedBiomarkers([]);
    setSelectedDisorders([]);
    setSelectedRegions([]);
    setBiomarkerSearchTerm('');
    setDisorderSearchTerm('');
    setRegionSearchTerm('');
  };

  return (
    <div className="search-section">
      <h2>Search by Biomarker</h2>
      
      <div className="search-controls">
        <div className="form-group">
          <label>Biomarker(s):</label>
          <div className="biomarker-select-container" ref={biomarkerDropdownRef}>
            <div className="biomarker-input-wrapper">
              <input
                type="text"
                value={biomarkerSearchTerm}
                onChange={(e) => {
                  setBiomarkerSearchTerm(e.target.value);
                  setIsBiomarkerDropdownOpen(true);
                }}
                onFocus={() => setIsBiomarkerDropdownOpen(true)}
                placeholder="Type to search biomarkers..."
                className="biomarker-search-input"
              />
              {isBiomarkerDropdownOpen && (
                <div className="biomarker-dropdown">
                  <div className="biomarker-bulk-actions">
                    <button
                      type="button"
                      onClick={toggleSelectAllFilteredBiomarkers}
                      className="bulk-action-btn"
                    >
                      {areAllFilteredBiomarkersSelected() ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedBiomarkers.length > 0 && (
                      <button
                        type="button"
                        onClick={selectNoneBiomarkers}
                        className="bulk-action-btn"
                      >
                        Select None
                      </button>
                    )}
                  </div>
                  
                  {filteredBiomarkers.length > 0 ? (
                    filteredBiomarkers.map(biomarker => (
                      <div
                        key={biomarker}
                        className={`biomarker-option ${
                          selectedBiomarkers.includes(biomarker) ? 'selected' : ''
                        }`}
                        onClick={() => handleBiomarkerSelect(biomarker)}
                      >
                        {biomarker}
                        {selectedBiomarkers.includes(biomarker) && (
                          <span className="checkmark">✓</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="no-options">No biomarkers found</div>
                  )}
                </div>
              )}
            </div>

            {selectedBiomarkers.length > 0 && (
              <div className="selected-biomarkers">
                <div className="selected-header">
                  <span>Selected Biomarkers ({selectedBiomarkers.length})</span>
                  <div className="selected-actions">
                    <button 
                      type="button" 
                      onClick={selectAllBiomarkers}
                      className="action-btn secondary"
                      title="Select all biomarkers"
                    >
                      Select All
                    </button>
                    <button 
                      type="button" 
                      onClick={clearAllBiomarkers}
                      className="clear-all-btn"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <div className="selected-tags">
                  {selectedBiomarkers.map(biomarker => (
                    <span key={biomarker} className="selected-tag">
                      {biomarker}
                      <button 
                        type="button" 
                        onClick={() => removeBiomarker(biomarker)}
                        className="remove-tag"
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>Disorder (optional):</label>
          <div className="biomarker-select-container" ref={disorderDropdownRef}>
            <div className="biomarker-input-wrapper">
              <input
                type="text"
                value={disorderSearchTerm}
                onChange={(e) => {
                  setDisorderSearchTerm(e.target.value);
                  setIsDisorderDropdownOpen(true);
                }}
                onFocus={() => setIsDisorderDropdownOpen(true)}
                placeholder="Type to search disorders..."
                className="biomarker-search-input"
              />
              {isDisorderDropdownOpen && (
                <div className="biomarker-dropdown">
                  <div className="biomarker-bulk-actions">
                    <button
                      type="button"
                      onClick={toggleSelectAllFilteredDisorders}
                      className="bulk-action-btn"
                    >
                      {areAllFilteredDisordersSelected() ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedDisorders.length > 0 && (
                      <button
                        type="button"
                        onClick={selectNoneDisorders}
                        className="bulk-action-btn"
                      >
                        Select None
                      </button>
                    )}
                  </div>
                  
                  {filteredDisorders.length > 0 ? (
                    filteredDisorders.map(disorder => (
                      <div
                        key={disorder}
                        className={`biomarker-option ${
                          selectedDisorders.includes(disorder) ? 'selected' : ''
                        }`}
                        onClick={() => handleDisorderSelect(disorder)}
                      >
                        {disorder}
                        {selectedDisorders.includes(disorder) && (
                          <span className="checkmark">✓</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="no-options">No disorders found</div>
                  )}
                </div>
              )}
            </div>

            {selectedDisorders.length > 0 && (
              <div className="selected-biomarkers">
                <div className="selected-header">
                  <span>Selected Disorders ({selectedDisorders.length})</span>
                  <div className="selected-actions">
                    <button 
                      type="button" 
                      onClick={selectAllDisorders}
                      className="action-btn secondary"
                      title="Select all disorders"
                    >
                      Select All
                    </button>
                    <button 
                      type="button" 
                      onClick={clearAllDisorders}
                      className="clear-all-btn"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <div className="selected-tags">
                  {selectedDisorders.map(disorder => (
                    <span key={disorder} className="selected-tag">
                      {disorder}
                      <button 
                        type="button" 
                        onClick={() => removeDisorder(disorder)}
                        className="remove-tag"
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>Brain Region (optional):</label>
          <div className="biomarker-select-container" ref={regionDropdownRef}>
            <div className="biomarker-input-wrapper">
              <input
                type="text"
                value={regionSearchTerm}
                onChange={(e) => {
                  setRegionSearchTerm(e.target.value);
                  setIsRegionDropdownOpen(true);
                }}
                onFocus={() => setIsRegionDropdownOpen(true)}
                placeholder="Type to search regions..."
                className="biomarker-search-input"
              />
              {isRegionDropdownOpen && (
                <div className="biomarker-dropdown">
                  <div className="biomarker-bulk-actions">
                    <button
                      type="button"
                      onClick={toggleSelectAllFilteredRegions}
                      className="bulk-action-btn"
                    >
                      {areAllFilteredRegionsSelected() ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedRegions.length > 0 && (
                      <button
                        type="button"
                        onClick={selectNoneRegions}
                        className="bulk-action-btn"
                      >
                        Select None
                      </button>
                    )}
                  </div>
                  
                  {filteredRegions.length > 0 ? (
                    filteredRegions.map(region => (
                      <div
                        key={region}
                        className={`biomarker-option ${
                          selectedRegions.includes(region) ? 'selected' : ''
                        }`}
                        onClick={() => handleRegionSelect(region)}
                      >
                        {region}
                        {selectedRegions.includes(region) && (
                          <span className="checkmark">✓</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="no-options">No regions found</div>
                  )}
                </div>
              )}
            </div>

            {selectedRegions.length > 0 && (
              <div className="selected-biomarkers">
                <div className="selected-header">
                  <span>Selected Regions ({selectedRegions.length})</span>
                  <div className="selected-actions">
                    <button 
                      type="button" 
                      onClick={selectAllRegions}
                      className="action-btn secondary"
                      title="Select all regions"
                    >
                      Select All
                    </button>
                    <button 
                      type="button" 
                      onClick={clearAllRegions}
                      className="clear-all-btn"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <div className="selected-tags">
                  {selectedRegions.map(region => (
                    <span key={region} className="selected-tag">
                      {region}
                      <button 
                        type="button" 
                        onClick={() => removeRegion(region)}
                        className="remove-tag"
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="button-group">
          <button onClick={handleSearch} className="search-btn">
            Search ({selectedBiomarkers.length + selectedDisorders.length + selectedRegions.length})
          </button>
          <button onClick={clearResults} className="clear-btn">Clear All</button>
        </div>
      </div>

      <div className="results">
        <div className="result-section">
          <h3>Where Biomarker Increases (↑)</h3>
          {increasedResults.length > 0 ? (
            <table className="results-table">
              <thead>
                <tr>
                  <th>Biomarker</th>
                  <th>Disorder</th>
                  <th>Brain Region</th>
                </tr>
              </thead>
              <tbody>
                {increasedResults.map((result, index) => (
                  <tr key={index}>
                    <td>{result.biomarker}</td>
                    <td>{result.disorder}</td>
                    <td>{result.region}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No results found for increased biomarkers</p>
          )}
        </div>

        <div className="result-section">
          <h3>Where Biomarker Decreases (↓)</h3>
          {decreasedResults.length > 0 ? (
            <table className="results-table">
              <thead>
                <tr>
                  <th>Biomarker</th>
                  <th>Disorder</th>
                  <th>Brain Region</th>
                </tr>
              </thead>
              <tbody>
                {decreasedResults.map((result, index) => (
                  <tr key={index}>
                    <td>{result.biomarker}</td>
                    <td>{result.disorder}</td>
                    <td>{result.region}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No results found for decreased biomarkers</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BiomarkerSearch;